import { useCallback, useEffect, useState } from 'react'
import { contractsApi, type CoralyState, type PortalPhase } from '../api/contracts'
import { errMsg } from '../lib/financeFormat'
import { Button, Callout, Card, CardContent, CardHeader, StatusPill, TextField } from './ui'

function when(iso: string | null | undefined): string {
  if (!iso) return '-'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
}

/**
 * Sezione "Portale Coraly" della pratica: fase attuale, storico delle fasi e messaggi della chat della card, in sola lettura
 * (testo semplice, mai HTML). Compare solo se la pratica e' collegata; lo staff vede anche il form di collegamento.
 * Non mostra mai nomi di mandanti o portali oltre a "Portale".
 * `onPortalPhase` riceve la fase a ogni lettura (anche dopo «Aggiorna ora»): la scheda la mette accanto allo stato.
 */
export function ContractCoralyPanel({ contractId, canManage, onPortalPhase }: {
  contractId: number
  canManage: boolean
  onPortalPhase?: ((phase: PortalPhase | null) => void) | undefined
}) {
  const [state, setState] = useState<CoralyState | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [processId, setProcessId] = useState('')
  const [cardId, setCardId] = useState('')

  const load = useCallback(async () => {
    try {
      setState(await contractsApi.coraly(contractId))
      setError('')
    } catch {
      setState(null)
    }
  }, [contractId])

  useEffect(() => { void load() }, [load])
  useEffect(() => { onPortalPhase?.(state?.portalPhase ?? null) }, [state, onPortalPhase])

  const run = async (action: () => Promise<CoralyState | void>) => {
    setBusy(true)
    setError('')
    try {
      const r = await action()
      setState(r ?? { linked: false })
    } catch (e) {
      setError(errMsg(e, 'Operazione non riuscita.'))
    } finally {
      setBusy(false)
    }
  }

  if (!state) return null
  if (!state.linked && !canManage) return null

  const events = state.events ?? []
  const phases = events.filter(e => e.type === 'card.phase_change')
  const messages = events.filter(e => e.type === 'card.comment' && e.text)

  return (
    <Card variant="outlined" className="mb-4">
      <CardHeader icon="hub" title="Portale" />
      <CardContent>
        {error && <Callout tone="error">{error}</Callout>}
        {state.suspended && <Callout tone="warning">{state.suspendedMessage || 'Collegamento con il portale sospeso.'}</Callout>}
        {state.linked && (
          <>
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <span>Fase attuale: {state.phaseName ? <StatusPill tone="info">{state.phaseName}</StatusPill> : <em>non ancora disponibile</em>}</span>
              <span className="text-muted md-typescale-body-small">Ultimo aggiornamento: {when(state.lastSyncAt)}</span>
              {canManage && (
                <>
                  {!state.suspended && <Button variant="outlined" disabled={busy} onClick={() => void run(() => contractsApi.coralySync(contractId))}>Aggiorna ora</Button>}
                  <Button variant="text" disabled={busy} onClick={() => void run(async () => { await contractsApi.coralyUnlink(contractId) })}>Scollega</Button>
                </>
              )}
            </div>
            {canManage && state.lastError && <Callout tone="warning">{state.lastError}</Callout>}

            <h4 className="md-typescale-title-small">Storico fasi</h4>
            {phases.length === 0 ? <p className="text-muted">Nessun cambio di fase registrato.</p> : (
              <ul>
                {phases.map(e => (
                  <li key={e.id}>{when(e.occurredAt)}: {e.phaseFrom ? `${e.phaseFrom} → ` : ''}{e.phaseTo ?? ''}</li>
                ))}
              </ul>
            )}

            <h4 className="md-typescale-title-small">Messaggi</h4>
            {messages.length === 0 ? <p className="text-muted">Nessun messaggio.</p> : (
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {messages.map(e => (
                  <li key={e.id} className="mb-2">
                    <div className="text-muted md-typescale-body-small">{when(e.occurredAt)}{e.authorName ? ` - ${e.authorName}` : ''}</div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{e.text}</div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {canManage && !state.linked && <p className="text-muted">Nessuna card collegata a questa pratica.</p>}
        {canManage && (
          <form
            className="d-flex flex-wrap align-items-end gap-2 mt-3"
            onSubmit={ev => { ev.preventDefault(); void run(() => contractsApi.coralyLink(contractId, processId.trim(), cardId.trim())) }}
          >
            <TextField label="Processo" value={processId} onChange={e => setProcessId(e.target.value)} />
            <TextField label="Card" value={cardId} onChange={e => setCardId(e.target.value)} />
            <Button variant="filled" type="submit" disabled={busy || !processId.trim() || !cardId.trim()}>{state.linked ? 'Cambia collegamento' : 'Collega'}</Button>
          </form>
        )}
      </CardContent>
    </Card>
  )
}
